"use client";

import { useState, useRef, useEffect, useMemo } from "react";
import { 
  Folder, 
  ChevronRight, 
  ChevronDown, 
  Plus, 
  FolderPlus,
  MoreVertical,
  FileText,
  Trash2,
  Edit2,
  X,
  Check,
  GripVertical,
  Search,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useWorkspace, WorkspaceFile } from "@/lib/workspace-context";
import { toast } from "@/components/ui/toast";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

interface TreeFileNode extends WorkspaceFile {
  children?: TreeFileNode[];
}

export function FileExplorer() {
  const { 
    files, 
    createFile, 
    deleteFile, 
    openFile, 
    leftPane, 
    rightPane, 
    activePane, 
    updateFileTitle,
    selectedProject,
    selectedMember,
    selectedTimeframe,
    tasks
  } = useWorkspace();
  
  const [creatingType, setCreatingType] = useState<"file" | "folder" | null>(null);
  const [creatingParentId, setCreatingParentId] = useState<string | undefined>(undefined);
  const [inputName, setInputName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Group and Filter files into tree hierarchy
  const fileTree = useMemo(() => {
    // 1. Get filtered list of active files
    const matchedFiles = files.filter(f => {
      // Search query filtering
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const nameMatches = f.name.toLowerCase().includes(q);
        if (!nameMatches) {
          if (f.type === "folder") {
            const hasMatchingDescendant = (parentId: string): boolean => {
              const children = files.filter(child => child.parentId === parentId);
              return children.some(child => 
                child.name.toLowerCase().includes(q) || 
                (child.type === "folder" && hasMatchingDescendant(child.id))
              );
            };
            if (!hasMatchingDescendant(f.id)) return false;
          } else {
            return false;
          }
        }
      }

      if (f.type === "folder") return true; // Keep folders initially, prune later
      
      // Filter by Project
      if (selectedProject !== "all" && f.project !== selectedProject) return false;
      
      // Filter by Member (at least one task assigned to member in this file)
      if (selectedMember !== "all") {
        const hasTaskForMember = tasks.some(
          (t) => t.document_id === f.documentId && t.assignee_name === selectedMember
        );
        if (!hasTaskForMember) return false;
      }
      
      // Filter by Timeframe (at least one task in this timeframe in this file)
      if (selectedTimeframe !== "all") {
        const hasTaskInTimeframe = tasks.some(
          (t) => t.document_id === f.documentId && t.timeframe === selectedTimeframe
        );
        if (!hasTaskInTimeframe) return false;
      }
      
      return true;
    });

    const fileMap = new Map<string, TreeFileNode>();
    const roots: TreeFileNode[] = [];

    // First pass: create nodes
    matchedFiles.forEach(f => {
      fileMap.set(f.id, { ...f, children: [] });
    });

    // Second pass: associate children with parents
    matchedFiles.forEach(f => {
      const node = fileMap.get(f.id)!;
      if (f.parentId && fileMap.has(f.parentId)) {
        const parent = fileMap.get(f.parentId)!;
        parent.children = parent.children || [];
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Prune empty folders recursively ONLY when a search or filter is active
    const hasActiveFilter =
      Boolean(searchQuery.trim()) ||
      selectedProject !== "all" ||
      selectedMember !== "all" ||
      selectedTimeframe !== "all";

    const pruneEmptyFolders = (nodes: TreeFileNode[]): TreeFileNode[] => {
      return nodes.filter(node => {
        if (node.type === "file") return true;
        if (node.children) {
          node.children = pruneEmptyFolders(node.children);
          return node.children.length > 0;
        }
        return false;
      });
    };

    const prunedRoots = hasActiveFilter ? pruneEmptyFolders(roots) : roots;

    // Sort folders first, then alphabetically
    const sortNodes = (nodes: TreeFileNode[]): TreeFileNode[] => {
      return nodes.sort((a, b) => {
        if (a.type !== b.type) {
          return a.type === "folder" ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
      }).map(n => {
        if (n.children && n.children.length > 0) {
          n.children = sortNodes(n.children);
        }
        return n;
      });
    };

    return sortNodes(prunedRoots);
  }, [files, selectedProject, selectedMember, selectedTimeframe, tasks, searchQuery]);

  useEffect(() => {
    if (creatingType && inputRef.current) {
      inputRef.current.focus();
    }
  }, [creatingType]);

  const handleCreate = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!inputName.trim()) {
      setCreatingType(null);
      return;
    }

    createFile(inputName, creatingParentId, creatingType === "folder", selectedProject !== "all" ? selectedProject : "Wiki Arsenal");
    setInputName("");
    setCreatingType(null);
    setCreatingParentId(undefined);
  };

  const cancelCreate = () => {
    setInputName("");
    setCreatingType(null);
    setCreatingParentId(undefined);
  };

  return (
    <div className="flex flex-col h-full select-none">
      {/* File Explorer Header */}
      <div className="flex items-center justify-between px-4 h-9 border-b border-border/10 bg-secondary/5 shrink-0">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 flex items-center gap-1.5 min-w-0">
          <span className="shrink-0">Explorador</span>
          {selectedProject !== "all" && (
            <span className="text-[9px] text-primary bg-primary/10 border border-primary/20 rounded px-1.5 py-0.5 font-bold uppercase truncate max-w-[120px] select-none" title={`Filtrado por: ${selectedProject}`}>
              {selectedProject}
            </span>
          )}
        </span>
        <div className="flex items-center gap-0.5">
          <button 
            onClick={() => {
              setCreatingType("file");
              setCreatingParentId(undefined);
            }}
            className="p-1 hover:bg-secondary/60 rounded-md text-muted-foreground hover:text-foreground transition-all active:scale-[0.95]"
            title="Nueva nota en raíz"
            aria-label="Nueva nota"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button 
            onClick={() => {
              setCreatingType("folder");
              setCreatingParentId(undefined);
            }}
            className="p-1 hover:bg-secondary/60 rounded-md text-muted-foreground hover:text-foreground transition-all active:scale-[0.95]"
            title="Nueva carpeta en raíz"
            aria-label="Nueva carpeta"
          >
            <FolderPlus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      
      {/* Search Bar */}
      <div className="px-3 py-2 border-b border-border/10 bg-secondary/5 shrink-0">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/60" />
          <input
            type="text"
            placeholder="Buscar notas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-8 pl-8 pr-7 bg-background/50 hover:bg-background/80 focus:bg-background border border-border/40 focus:border-primary/50 text-[11px] rounded-lg outline-none transition-all placeholder:text-muted-foreground/50"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-secondary rounded-md text-muted-foreground hover:text-foreground transition-all"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      
      {/* Root level creation input */}
      {creatingType && !creatingParentId && (
        <form 
          onSubmit={handleCreate}
          className="mx-3 my-1 flex items-center gap-1.5 p-1 bg-secondary/30 rounded-md border border-primary/30"
        >
          {creatingType === "folder" ? (
            <Folder className="w-3.5 h-3.5 text-primary/70 shrink-0" />
          ) : (
            <FileText className="w-3.5 h-3.5 text-primary/70 shrink-0" />
          )}
          <input
            ref={inputRef}
            type="text"
            placeholder={creatingType === "folder" ? "Nueva carpeta..." : "Nueva nota..."}
            value={inputName}
            onChange={(e) => setInputName(e.target.value)}
            onBlur={() => setTimeout(cancelCreate, 200)}
            className="flex-1 bg-transparent text-xs text-foreground outline-none w-full min-w-0"
          />
        </form>
      )}

      {/* File Tree List */}
      <div className="px-1.5 space-y-[2px]">
        {fileTree.map((node) => (
          <FileExplorerNode 
            key={node.id} 
            node={node} 
            depth={0} 
            creatingType={creatingType}
            creatingParentId={creatingParentId}
            setCreatingType={setCreatingType}
            setCreatingParentId={setCreatingParentId}
            inputRef={inputRef}
            inputName={inputName}
            setInputName={setInputName}
            handleCreate={handleCreate}
            cancelCreate={cancelCreate}
          />
        ))}
        {fileTree.length === 0 && !creatingType && (
          <div className="text-center py-8 text-xs text-muted-foreground/40 italic px-4">
            {selectedProject !== "all" || selectedMember !== "all" || selectedTimeframe !== "all"
              ? "Ningún archivo coincide con los filtros aplicados"
              : "No hay notas creadas"}
          </div>
        )}
      </div>
    </div>
  );
}

interface NodeProps {
  node: TreeFileNode;
  depth: number;
  creatingType: "file" | "folder" | null;
  creatingParentId: string | undefined;
  setCreatingType: (type: "file" | "folder" | null) => void;
  setCreatingParentId: (id: string | undefined) => void;
  inputRef: React.RefObject<any>;
  inputName: string;
  setInputName: (val: string) => void;
  handleCreate: (e?: React.FormEvent) => void;
  cancelCreate: () => void;
}

function FileExplorerNode({ 
  node, 
  depth, 
  creatingType, 
  creatingParentId, 
  setCreatingType, 
  setCreatingParentId,
  inputRef,
  inputName,
  setInputName,
  handleCreate,
  cancelCreate
}: NodeProps) {
  const { openFile, deleteFile, moveFile, leftPane, rightPane, activePane, updateFileTitle, selectedProject, files, selectedFileIds, toggleSelectFile } = useWorkspace();
  const [isOpen, setIsOpen] = useState(depth === 0);
  const [pendingDelete, setPendingDelete] = useState<null | { id: string; name: string; descendants: number }>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const childCount = useMemo(() => {
    const countDescendants = (parentId: string): number => {
      const children = files.filter(f => f.parentId === parentId);
      let total = children.length;
      for (const child of children) {
        total += countDescendants(child.id);
      }
      return total;
    };
    return countDescendants(node.id);
  }, [files, node.id]);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(node.name.replace(".md", ""));
  const renameInputRef = useRef<HTMLInputElement>(null);

  const isFolder = node.type === "folder";
  
  // Highlight if active in the primary or secondary pane
  const isActiveLeft = leftPane.viewType === "editor" && leftPane.activeFileId === node.id;
  const isActiveRight = rightPane.viewType === "editor" && rightPane.activeFileId === node.id;
  const isSelected = isActiveLeft || isActiveRight;
  const isMultiSelected = selectedFileIds.includes(node.id);

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  const handleNodeClick = (e: React.MouseEvent) => {
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      e.preventDefault();
      toggleSelectFile(node.id, true);
      return;
    }
    if (selectedFileIds.length > 0) {
      toggleSelectFile(node.id, true);
      return;
    }
    if (isFolder) {
      setIsOpen(!isOpen);
    } else {
      openFile(node.id);
    }
  };

  const handleRenameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== node.name.replace(".md", "")) {
      updateFileTitle(node.id, trimmed);
    }
    setIsRenaming(false);
  };

  const handleRenameCancel = () => {
    setRenameValue(node.name.replace(".md", ""));
    setIsRenaming(false);
  };

  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData("application/x-wiki-file-id", node.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isFolder) return;
    if (!e.dataTransfer.types.includes("application/x-wiki-file-id")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!isFolder) return;
    e.preventDefault();
    setIsDragOver(false);
    const draggedId = e.dataTransfer.getData("application/x-wiki-file-id");
    if (!draggedId || draggedId === node.id) return;
    const success = moveFile(draggedId, node.id);
    if (success) {
      setIsOpen(true);
      toast.success("Movido", `"${draggedId === node.id ? node.name : "archivo"}" se movió a "${node.name}".`);
    }
  };

  return (
    <div className="w-full">
      <div
        className={cn(
          "w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-[13px] transition-all group relative cursor-pointer outline-none select-none",
          isSelected
            ? cn(
                "bg-primary/10 text-primary font-semibold shadow-sm",
                (isActiveLeft && activePane === "left") || (isActiveRight && activePane === "right")
                  ? "ring-1 ring-primary/30"
                  : "opacity-80"
              )
            : isMultiSelected
              ? "bg-primary/5 text-foreground ring-1 ring-primary/30"
              : "text-muted-foreground hover:bg-secondary/40 hover:text-foreground",
          isDragOver && "ring-2 ring-primary/50 bg-primary/5"
        )}
        style={{ paddingLeft: `${depth * 10 + 8}px` }}
        onClick={handleNodeClick}
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Drag handle (visible on hover) */}
        <GripVertical
          className="w-3 h-3 text-muted-foreground/30 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          aria-hidden="true"
        />

        {/* Toggle Folder Arrow */}
        <span className="w-3.5 h-3.5 flex items-center justify-center opacity-60">
          {isFolder ? (
            isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />
          ) : null}
        </span>

        {/* Node Icon */}
        {isFolder ? (
          <Folder className={cn("w-4 h-4 shrink-0", isOpen ? "text-primary/70" : "text-muted-foreground/50")} />
        ) : (
          <FileText className={cn("w-4 h-4 shrink-0", isSelected ? "text-primary/70" : "text-muted-foreground/40")} />
        )}

        {/* Drag-over indicator: pulsating + icon next to name */}
        {isDragOver && isFolder && (
          <span className="ml-1 w-4 h-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold animate-pulse shrink-0">
            +
          </span>
        )}
        
        {/* Title Text or Rename Input */}
        {isRenaming ? (
          <form onSubmit={handleRenameSubmit} onClick={e => e.stopPropagation()} className="flex-1 flex items-center">
            <input
              ref={renameInputRef}
              type="text"
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRenameCancel();
                }
              }}
              className="w-full bg-background border border-border text-xs px-1 rounded text-foreground outline-none focus:border-primary"
            />
          </form>
        ) : (
          <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="truncate tracking-tight text-left">{node.name.replace(".md", "")}</span>
              {isFolder && childCount > 0 && (
                <span className="text-[9px] px-1.5 bg-secondary text-muted-foreground/80 rounded font-semibold tabular-nums shrink-0 border border-border/10 select-none">
                  {childCount}
                </span>
              )}
            </div>
            {selectedProject === "all" && node.project && node.type === "file" && (
              <span className="text-[9px] px-1.5 py-0.2 rounded bg-secondary/80 text-muted-foreground/80 border border-border/20 shrink-0 font-medium max-w-[70px] truncate select-none" title={`Proyecto: ${node.project}`}>
                {node.project}
              </span>
            )}
          </div>
        )}
        
        {/* Action Buttons (Hover) */}
        {!isRenaming && (
          <div className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 ml-auto relative z-10" onClick={e => e.stopPropagation()}>
            {isFolder && (
              <>
                <button 
                  onClick={() => {
                    setIsOpen(true);
                    setCreatingType("file");
                    setCreatingParentId(node.id);
                  }}
                  className="p-0.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-all"
                  title="Nueva Nota"
                >
                  <Plus className="w-3 h-3" />
                </button>
                <button 
                  onClick={() => {
                    setIsOpen(true);
                    setCreatingType("folder");
                    setCreatingParentId(node.id);
                  }}
                  className="p-0.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-all"
                  title="Nueva Carpeta"
                >
                  <FolderPlus className="w-3 h-3" />
                </button>
              </>
            )}
            <button
              onClick={() => setIsRenaming(true)}
              className="p-0.5 hover:bg-secondary rounded text-muted-foreground hover:text-foreground transition-all"
              title="Renombrar"
              aria-label={`Renombrar ${node.name.replace(".md", "")}`}
            >
              <Edit2 className="w-3 h-3" aria-hidden="true" />
            </button>
            <button
              onClick={() => {
                if (node.type === "folder" && childCount > 0) {
                  setPendingDelete({ id: node.id, name: node.name, descendants: childCount });
                } else {
                  deleteFile(node.id);
                  toast.success("Eliminado", `"${node.name.replace(".md", "")}" fue eliminado.`);
                }
              }}
              className="p-0.5 hover:bg-secondary rounded text-muted-foreground hover:text-destructive transition-all"
              title="Eliminar"
              aria-label={`Eliminar ${node.name.replace(".md", "")}`}
            >
              <Trash2 className="w-3 h-3" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title="Eliminar carpeta"
        description={
          pendingDelete
            ? `La carpeta "${pendingDelete.name.replace(".md", "")}" contiene ${pendingDelete.descendants} elemento(s) que también se eliminarán. Esta acción no se puede deshacer.`
            : ""
        }
        confirmText="Sí, eliminar todo"
        cancelText="Cancelar"
        variant="destructive"
        onConfirm={() => {
          if (pendingDelete) {
            deleteFile(pendingDelete.id);
            toast.success("Eliminado", `"${pendingDelete.name.replace(".md", "")}" y su contenido fueron eliminados.`);
          }
        }}
      />

      {/* Folder Children / Nested creation input */}
      <AnimatePresence initial={false}>
        {isFolder && isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            {/* Folder level creation input */}
            {creatingType && creatingParentId === node.id && (
              <form 
                onSubmit={handleCreate}
                className="flex items-center gap-1.5 p-1 bg-secondary/30 rounded-md border border-primary/30 my-0.5"
                style={{ marginLeft: `${(depth + 1) * 10 + 8}px` }}
                onClick={e => e.stopPropagation()}
              >
                {creatingType === "folder" ? (
                  <Folder className="w-3.5 h-3.5 text-primary/70 shrink-0" />
                ) : (
                  <FileText className="w-3.5 h-3.5 text-primary/70 shrink-0" />
                )}
                <input
                  ref={inputRef}
                  type="text"
                  placeholder={creatingType === "folder" ? "Nueva carpeta..." : "Nueva nota..."}
                  value={inputName}
                  onChange={(e) => setInputName(e.target.value)}
                  onBlur={() => setTimeout(cancelCreate, 200)}
                  className="flex-1 bg-transparent text-xs text-foreground outline-none w-full min-w-0"
                />
              </form>
            )}

            {node.children && node.children.map((child) => (
              <FileExplorerNode 
                key={child.id} 
                node={child} 
                depth={depth + 1} 
                creatingType={creatingType}
                creatingParentId={creatingParentId}
                setCreatingType={setCreatingType}
                setCreatingParentId={setCreatingParentId}
                inputRef={inputRef}
                inputName={inputName}
                setInputName={setInputName}
                handleCreate={handleCreate}
                cancelCreate={cancelCreate}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
